import { ref } from "vue";

/**
 * Shared async save/reset scaffolding used by Settings-dialog tabs
 * (TagManagement, StatusNotificationManagement, …).
 *
 * Encapsulates the loading flag, error / success message refs, 3-second
 * success-message timeout, and the try/catch/finally boilerplate so each
 * caller only has to pass the actual work.
 *
 * Exposes refs that templates can bind directly for button :loading / alert
 * display. Callers invoke run(fn, opts) with the specific service call + any
 * local state updates inside the async fn; on throw, err.message is
 * surfaced (with an opts.errorText fallback).
 */
export function useSettingsSaveFlow() {
  const saving = ref(false);
  const error = ref("");
  const successMsg = ref("");

  async function run(fn, { successText = "Saved.", errorText = "Failed to save. Please try again." } = {}) {
    saving.value = true;
    error.value = "";
    successMsg.value = "";
    try {
      await fn();
      successMsg.value = successText;
      setTimeout(() => (successMsg.value = ""), 3000);
    } catch (err) {
      error.value = err?.message || errorText;
    } finally {
      saving.value = false;
    }
  }

  return { saving, error, successMsg, run };
}
